import type { ListItemBlock } from '@atlas/renderer';
import extractNodeTextRecursively from '@atlas/tool/box/slate/extractNodeTextRecursively';
import type { ListBlock } from '@atlas/tool/box/slate/isListBlock';
import isHtmlElementNode from '@atlas/tool/box/dom/isHtmlElementNode';
import isListHtmlElement from '@atlas/tool/box/dom/isListHtmlElement';

export default function extractListBlockFromListElement(
    listElement: HTMLUListElement | HTMLOListElement,
): ListBlock | null {
    const listItems: Array<ListBlock | ListItemBlock> = [];

    for (let i = 0; i < listElement.childNodes.length; i++) {
        const item = listElement.childNodes[i];
        const itemIsElement = isHtmlElementNode(item);

        if (itemIsElement) {
            const childNodes = Array.from(item.childNodes);
            const listNodes = childNodes.filter(isListHtmlElement);
            listNodes.forEach((node) => node.remove());

            const leaves = extractNodeTextRecursively(item.childNodes);
            listItems.push({
                type: 'list-item',
                children: leaves.length ? leaves : [{ text: '' }],
            });

            if (listNodes.length === 1) {
                const list = extractListBlockFromListElement(listNodes[0]);
                if (list) listItems.push(list);
            } else if (listNodes.length > 1) {
                const lists = listNodes
                    .map(extractListBlockFromListElement)
                    .filter((result): result is ListBlock => !!result);

                if (lists.length) {
                    const list = lists[0];
                    lists.slice(1).forEach(({ children }) => {
                        list.children.push(...children);
                    });
                }
            }
        }
    }

    if (!listItems.length) return null;

    return {
        type: isHtmlElementNode(listElement, 'OL') ? 'numbered-list' : 'bulleted-list',
        children: listItems,
        ...(listElement instanceof HTMLOListElement &&
            listElement.start &&
            listElement.start > 1 && { start: listElement.start }),
    };
}

// #region Tests

if (import.meta.vitest) {
    const { describe, it } = import.meta.vitest;
    describe('extractListBlockFromListElement', async () => {
        it.todo('🤷🏼');
    });
}

// #endregion
